import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Shield, Loader2, CheckCircle2, AlertTriangle, LogOut,
  LogIn, ExternalLink, Clock, Globe, Monitor,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function BrandShell({ children }) {
  return (
    <div className="min-h-screen bg-[#F5F3FA] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center p-1 shadow ring-1 ring-border">
            <img src="/abelia-logo.jpg" alt="Abelia" className="w-full h-full object-contain rounded-lg" />
          </div>
          <div>
            <div className="font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              Abelia · Security
            </div>
            <div className="accp-label text-[9px]">Review new-device sign-in</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm ring-1 ring-border p-6 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export default function ReviewAlert() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const { user } = useAuth();

  // Flow state: "validating" | "ready" | "invalid" | "expired" | "used"
  //             | "confirming" | "done" | "error" | "auth_required" | "step_up"
  const [phase, setPhase] = useState("validating");
  const [details, setDetails] = useState(null);
  const [message, setMessage] = useState("");
  const [revokedCount, setRevokedCount] = useState(0);

  const reviewHref = useMemo(() => (
    `/login?next=${encodeURIComponent(
      "/account/devices/review-alert?token=" + token,
    )}`
  ), [token]);

  // 1. On mount → validate the token (read-only, no side effects)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token) {
        setPhase("invalid");
        setMessage("This link is missing a security token. Please reopen it from the email.");
        return;
      }
      try {
        const r = await api.post("/auth/device-alert/validate", { token });
        if (cancelled) return;
        setDetails(r.data);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const errCode = typeof detail === "object" ? detail?.error : null;
        const errMsg = typeof detail === "object" ? detail?.message : null;
        if (status === 429 && errCode === "rate_limited") {
          setPhase("error");
          setMessage(errMsg || "Too many requests. Please try again shortly.");
        } else if (errCode === "token_expired") {
          setPhase("expired");
          setMessage(errMsg || "This review link has expired. Links are valid for 15 minutes.");
        } else if (errCode === "token_already_used") {
          setPhase("used");
          setMessage(errMsg || "This review link has already been used.");
        } else if (status === 410) {
          // Fallback if backend ever returns a legacy non-structured 410.
          setPhase("expired");
          setMessage(errMsg || "This review link has expired. Links are valid for 15 minutes.");
        } else {
          setPhase("invalid");
          setMessage(errMsg || formatApiError(e));
        }
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doConfirm = async () => {
    setPhase("confirming");
    try {
      const r = await api.post("/auth/device-alert/confirm", { token });
      setRevokedCount(r.data?.revoked_count ?? 0);
      setPhase("done");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.error : null;
      const errMsg = typeof detail === "object" ? detail?.message : null;
      if (errCode === "auth_required") {
        setPhase("auth_required");
      } else if (errCode === "step_up_required") {
        setPhase("step_up");
      } else if (errCode === "token_already_used") {
        setPhase("used");
        setMessage(errMsg || "This review link has already been used.");
      } else if (errCode === "token_expired") {
        setPhase("expired");
        setMessage(errMsg || "This review link has expired.");
      } else if (errCode === "user_mismatch" || status === 403) {
        setPhase("error");
        setMessage(errMsg || "This link isn't for your account. Please sign in with the correct account.");
      } else if (status === 429 && errCode === "rate_limited") {
        setPhase("error");
        setMessage(errMsg || "Too many requests. Please try again shortly.");
      } else {
        setPhase("error");
        setMessage(errMsg || formatApiError(e));
      }
    }
  };

  // --- View builders -------------------------------------------------------

  if (phase === "validating") {
    return (
      <BrandShell>
        <Card className="text-center text-sm text-muted-foreground" >
          <Loader2 size={18} className="inline animate-spin mr-2" />
          Validating your security link…
        </Card>
      </BrandShell>
    );
  }

  if (phase === "invalid" || phase === "expired" || phase === "used") {
    const title = phase === "expired" ? "Link expired"
                : phase === "used" ? "Link already used"
                : "Invalid link";
    return (
      <BrandShell>
        <Card>
          <div className="flex items-start gap-3" data-testid={`review-alert-${phase}`}>
            <AlertTriangle size={22} className="text-[#B77B1C] shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                {title}
              </h2>
              <p className="text-sm text-muted-foreground mt-2">{message}</p>
              <p className="text-sm text-muted-foreground mt-3">
                For your security, please sign in and review your devices directly.
              </p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <button
                  onClick={() => nav("/account/devices")}
                  className="accp-btn-primary"
                  data-testid="review-alert-go-devices"
                >
                  <Shield size={14} /> Go to Devices &amp; Sessions
                </button>
                <a href="/login" className="accp-btn-ghost" data-testid="review-alert-login-fallback">
                  <LogIn size={14} /> Sign in
                </a>
              </div>
            </div>
          </div>
        </Card>
      </BrandShell>
    );
  }

  if (phase === "done") {
    return (
      <BrandShell>
        <Card>
          <div className="flex items-start gap-3" data-testid="review-alert-done">
            <CheckCircle2 size={22} className="text-[#6EB83B] shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                You're secure
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                {revokedCount > 0
                  ? <>We signed out <b>{revokedCount}</b> other {revokedCount === 1 ? "device" : "devices"}.
                    They'll need to sign in again to regain access.</>
                  : "No other devices were signed in. Your account is safe."}
              </p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <button
                  onClick={() => nav("/account/devices")}
                  className="accp-btn-primary"
                  data-testid="review-alert-view-devices"
                >
                  <Shield size={14} /> Review all devices
                </button>
              </div>
            </div>
          </div>
        </Card>
      </BrandShell>
    );
  }

  if (phase === "auth_required") {
    return (
      <BrandShell>
        <Card>
          <div className="flex items-start gap-3" data-testid="review-alert-auth-required">
            <LogIn size={22} className="text-primary shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                Please sign in to continue
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                For your security, you need to be signed in before we can sign out other devices.
                We'll bring you right back to this page after you sign in.
              </p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <a href={reviewHref} className="accp-btn-primary" data-testid="review-alert-sign-in">
                  <LogIn size={14} /> Sign in and continue
                </a>
                <button
                  onClick={() => nav("/account/devices")}
                  className="accp-btn-ghost"
                  data-testid="review-alert-go-devices"
                >
                  <Shield size={14} /> Review devices instead
                </button>
              </div>
            </div>
          </div>
        </Card>
      </BrandShell>
    );
  }

  if (phase === "step_up") {
    return (
      <BrandShell>
        <Card>
          <div className="flex items-start gap-3" data-testid="review-alert-step-up">
            <Shield size={22} className="text-[#B77B1C] shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                Extra verification required
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                This link was sent more than 10 minutes ago and the session you're using doesn't
                match the one it references. Please sign in again so we can verify it's really you,
                then you can complete the review.
              </p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <a href={reviewHref} className="accp-btn-primary" data-testid="review-alert-re-sign-in">
                  <LogIn size={14} /> Sign in to verify
                </a>
                <button
                  onClick={() => nav("/account/devices")}
                  className="accp-btn-ghost"
                  data-testid="review-alert-go-devices"
                >
                  <Shield size={14} /> Review devices instead
                </button>
              </div>
            </div>
          </div>
        </Card>
      </BrandShell>
    );
  }

  if (phase === "error") {
    return (
      <BrandShell>
        <Card>
          <div className="flex items-start gap-3" data-testid="review-alert-error">
            <AlertTriangle size={22} className="text-[#B71C1C] shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                Something went wrong
              </h2>
              <p className="text-sm text-muted-foreground mt-2">{message}</p>
              <div className="mt-5 flex gap-2 flex-wrap">
                <button onClick={doConfirm} className="accp-btn-primary" data-testid="review-alert-retry">
                  Try again
                </button>
                <button
                  onClick={() => nav("/account/devices")}
                  className="accp-btn-ghost"
                  data-testid="review-alert-go-devices"
                >
                  <Shield size={14} /> Review devices
                </button>
              </div>
            </div>
          </div>
        </Card>
      </BrandShell>
    );
  }

  // phase === "ready" or "confirming"
  const busy = phase === "confirming";
  return (
    <BrandShell>
      <Card data-testid="review-alert-ready">
        <div className="accp-label flex items-center gap-1 mb-2">
          <Shield size={11} /> Review new sign-in
        </div>
        <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          Was this you?
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          {user
            ? <>Signed in as <b>{user.email}</b>. We detected a new sign-in with
              "Remember me" on a device we hadn't seen before:</>
            : <>We detected a new sign-in with "Remember me" on a device we hadn't
              seen before. You'll need to sign in to take action.</>}
        </p>

        <div className="mt-4 rounded-xl bg-[#F5F3FA] ring-1 ring-border p-4 space-y-2 text-sm"
             data-testid="review-alert-details">
          <div className="flex items-center gap-2">
            <Monitor size={14} className="text-primary" />
            <span className="text-muted-foreground">Device</span>
            <span className="font-semibold text-primary ml-auto" data-testid="review-alert-device-label">
              {details?.device_label || "Unknown device"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Globe size={14} className="text-primary" />
            <span className="text-muted-foreground">Network</span>
            <span className="font-semibold text-primary ml-auto">
              IP {details?.masked_ip || "unknown"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-primary" />
            <span className="text-muted-foreground">Signed in</span>
            <span className="font-semibold text-primary ml-auto">
              {formatDate(details?.timestamp)}
            </span>
          </div>
          {details?.is_already_revoked && (
            <div className="pt-2 text-xs text-[#B77B1C]" data-testid="review-alert-already-revoked">
              This session was already signed out.
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={doConfirm}
            disabled={busy}
            className="accp-btn-primary w-full py-3 bg-[#B71C1C] hover:bg-[#8B1414]"
            data-testid="review-alert-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            Sign out all other devices
          </button>
          <button
            onClick={() => nav("/account/devices")}
            disabled={busy}
            className="accp-btn-ghost w-full py-3"
            data-testid="review-alert-this-was-me"
          >
            <CheckCircle2 size={14} /> This was me — no action needed
          </button>
          <button
            onClick={() => nav("/account/devices")}
            disabled={busy}
            className="accp-btn-ghost w-full py-3"
            data-testid="review-alert-review-devices"
          >
            <ExternalLink size={14} /> Review devices instead
          </button>
        </div>

        <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
          Revoking will sign out every other device that's currently remembered on
          your account. This action is logged for your security. Links expire after
          15 minutes and can only be used once.
        </p>
      </Card>
    </BrandShell>
  );
}
